/**
 * Centralized icon exports from lucide-react
 * Maps semantic names to icons for consistent usage
 * Performance optimized with memoized icon components
 */

/* eslint-disable react-refresh/only-export-components */

import { memo } from 'react';
import {
  // Navigation & UI
  Home,
  Users,
  Heart,
  BarChart3,
  User,
  ArrowLeft,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  MoreVertical,
  MoreHorizontal,
  X,
  Check,
  Plus,
  Clock,
  Menu,
  Settings,
  Bell,
  Copy,
  ClipboardList,
  ClipboardCheck,
  ListChecks,

  // Categories & Tags
  Moon,
  Sun,
  Star,
  Baby,
  Brain,
  HeartHandshake,
  MessageCircle,
  MessageCircleQuestion,
  Zap,
  Sparkles,
  Leaf,
  Droplets,
  Coffee,
  Activity,
  Footprints,
  Apple,
  BatteryLow,
  Wind,
  Flame,
  Trophy,
  Crown,
  Cloud,
  CloudOff,
  Gift,

  // Feedback & Communication
  AlertTriangle,
  Lock,
  FileText,
  BookOpen,
  Pill,

  // Actions & Feedback
  CheckCircle,
  AlertCircle,
  Info,
  Send,
  RefreshCw,
  Loader2,
  Target,
  RotateCcw,
  TrendingUp,
  TrendingDown,
  Calendar,
  Shield,

  // Coach & Wellness
  Smile,
  Frown,
  Meh,
  Sprout,
  Stethoscope,
  Mic,
  MicOff,

  // Media controls
  Play,
  Pause,
  Square,

  // Task List
  ListTodo,
  Trash2,
  Circle,
  Flag,
  Undo2,
  Pencil,
  PlusCircle,
  Pin,
  PinOff,

  // Additional
  LogOut,
  Wifi,
  WifiOff,
  Minus,
  Share2,
  Download,
  Smartphone,
  CalendarDays,
  CalendarPlus,
  Eye,
  EyeOff,
  ArrowRightLeft,
  Lightbulb,
  Clipboard,
  Inbox,
  MapPin,
  Link,
  HandHeart,
  HandMetal,
  Handshake,
  ThumbsUp,
  ThumbsDown,
  SkipForward,
  Briefcase,
  House,
  BedDouble,
  HeartCrack,
  StretchHorizontal,
  Timer,
  Hourglass,
  Camera,
  ImageIcon,
  Image,
  Images,
  Replace,
  Upload,
  Search,
  Filter,
  Building2,
  Edit3,
  FlaskConical,
  Syringe,
  Tag,
  MessageSquareText,
  ZoomIn,
  ZoomOut,
  GraduationCap,
  ExternalLink,
  StickyNote,

  type LucideIcon,
} from 'lucide-react';

// Re-export all icons
export {
  // Navigation & UI
  Home,
  Users,
  Heart,
  BarChart3,
  User,
  ArrowLeft,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  MoreVertical,
  MoreHorizontal, // Horizontal ellipsis menu ⋯
  X,
  Check,
  Plus,
  Clock,
  Menu,
  Settings,
  Bell,
  Copy,
  ClipboardList,
  ClipboardCheck,
  ListChecks,

  // Categories & Tags
  Moon,        // Sleep 💤
  Sun,         // Morning ☀️
  Star,        // Night ⭐
  Baby,        // Newborn/Feeding 🍼👶
  Brain,       // Mental health 🧠
  HeartHandshake, // Partner 💑
  MessageCircle,  // General/Chat 💬
  MessageCircleQuestion, // Q&A ❓
  Zap,         // Quick wins ⚡
  Sparkles,    // Highlights ✨
  Leaf,        // Self-care/Calm 🌿
  Droplets,    // Hydration 💧
  Coffee,      // Energy ☕
  Activity,    // Exercise/Movement
  Footprints,  // Movement/Walking 🚶
  Apple,       // Nutrition 🍎
  Wind,        // Breathing 🌬️
  Flame,       // Fire/Energy 🔥
  Trophy,      // Achievement 🏆
  Crown,       // Top/Best 👑
  Cloud,       // Weather/Mood ☁️
  CloudOff,    // Offline/Disconnected ☁️❌
  Gift,        // Reward/Gift 🎁

  // Feedback & Communication
  AlertTriangle, // Warning ⚠️
  Lock,        // Privacy 🔒
  FileText,    // Content 📝
  Pill,        // Medicine 💊

  // Actions & Feedback
  CheckCircle, // Success ✅
  AlertCircle, // Alert
  BookOpen,    // Document/source
  Info,        // Info
  Send,        // Send message
  RefreshCw,   // Refresh
  Loader2,     // Loading spinner
  Target,      // Goal 🎯
  RotateCcw,   // Reset
  TrendingUp,  // Progress up 📈
  TrendingDown, // Progress down 📉
  Calendar,    // Calendar 📅
  Shield,      // Privacy/Security 🛡️

  // Coach & Wellness
  Smile,       // Happy mood
  Frown,       // Low mood
  Meh,         // Neutral mood
  Sprout,      // Growth 🌱
  Stethoscope, // Health
  Mic,         // Voice input
  MicOff,      // Voice off

  // Media controls
  Play,        // Play ▶️
  Pause,       // Pause ⏸️
  Square,      // Stop ⏹️

  // Task List
  ListTodo,    // Task list 📋
  Trash2,      // Delete 🗑️
  Circle,      // Uncompleted ⭕
  Flag,        // Priority flag 🚩
  Undo2,       // Undo action ↩️
  Pencil,      // Edit ✏️
  PlusCircle,  // Add with circle ➕
  Pin,         // Pin to top 📌
  PinOff,      // Unpin

  // Additional
  LogOut,      // Sign out
  Wifi,        // Online status
  WifiOff,     // Offline status
  Minus,       // Minus/Subtract
  Share2 as Share, // Share action
  Share2,      // Raw Share2 export
  Download,    // Download/Install
  Smartphone,  // Mobile device
  CalendarDays, // Calendar view 📅
  CalendarPlus, // Add to calendar ➕📅
  Eye,         // Visibility on 👁️
  EyeOff,      // Visibility off 🙈
  ArrowRightLeft, // Swap/change
  Lightbulb,   // Idea/tip 💡
  Clipboard,   // Clipboard 📋
  Inbox,       // Inbox/notifications
  MapPin,      // Location
  Link,        // URL link
  HandHeart,   // Caring gesture
  HandMetal,   // Quick gesture
  Handshake,   // Partnership
  ThumbsUp,    // Positive feedback
  ThumbsDown,  // Negative feedback
  SkipForward, // Skip action
  Briefcase,   // Work/logistics
  House,       // Home/household
  BedDouble,   // Sleep/rest
  HeartCrack,  // Disconnection
  StretchHorizontal, // Space/stretch
  Timer,       // Timer
  Hourglass,   // Time passing
  Camera,      // Take photo
  ImageIcon,   // Image placeholder
  Image as FileImage, // File image (aliased)
  Images,      // Photo album
  Replace,     // Replace/swap
  Upload,      // Upload file
  Search,      // Search
  Filter,      // Filter
  Building2,   // Building/Organization
  Edit3,       // Edit (alternative)
  FlaskConical, // Lab/Flask
  Syringe,     // Injection/Vaccine
  Tag,         // Tag
  MessageSquareText as MessageSquare, // Message square
  ZoomIn,      // Zoom in
  ZoomOut,     // Zoom out
  GraduationCap, // Learning/Education 🎓
  ExternalLink, // External link / Open
  StickyNote,  // Note 📝

  // Type for icon components
  type LucideIcon,
};

// Category icon mapping
export const CATEGORY_ICONS = {
  sleep: Moon,
  rest: Moon,
  feeding: Baby,
  newborn: Baby,
  'mental-health': Brain,
  mental: Brain,
  'self-care': Leaf,
  'partner-support': HeartHandshake,
  partner: HeartHandshake,
  connection: HeartHandshake,
  'quick-wins': Zap,
  general: MessageCircle,
  hydration: Droplets,
  nutrition: Coffee,
  movement: Activity,
} as const;

// Icon name to component mapping for dynamic icon rendering
export const ICON_MAP: Record<string, LucideIcon> = {
  // Mood icons
  'smile': Smile,
  'frown': Frown,
  'meh': Meh,

  // Energy icons
  'battery-low': BatteryLow,
  'moon': Moon,
  'zap': Zap,

  // Stress icons
  'alert-circle': AlertCircle,

  // Category icons
  'droplets': Droplets,
  'apple': Apple,
  'activity': Activity,
  'brain': Brain,
  'heart': Heart,
  'cloud': Cloud,
  'leaf': Leaf,

  // State icons
  'sparkles': Sparkles,
  'sprout': Sprout,
  'star': Star,
  'sun': Sun,
  'trophy': Trophy,
  'crown': Crown,
  'flame': Flame,
  'target': Target,
  'gift': Gift,

  // Other
  'check': Check,
  'check-circle': CheckCircle,
  'home': Home,
  'chart': BarChart3,
  'user': User,
  'clock': Clock,
  'trending-up': TrendingUp,
};

// Dynamic icon component - memoized for performance
export const DynamicIcon = memo(function DynamicIcon({
  name,
  className = "w-4 h-4"
}: {
  name: string;
  className?: string;
}) {
  const IconComponent = ICON_MAP[name] || MessageCircle;
  return <IconComponent className={className} />;
});
